<template>
  <div ref="containerRef" :class="[$style.splitter, { [$style.resizing]: isResizing }]" :style="containerStyle">
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { computed, onMounted, provide, readonly, ref, watch } from "vue";

import type { SplitterContext, SplitterPanel } from "./splitterTypes";

const emit = defineEmits<(e: "resize", data: { panels: SplitterPanel[] }) => void>();

const containerRef = ref<HTMLElement>();
const panels = ref<SplitterPanel[]>([]);
const isResizing = ref(false);
const currentResizingPanel = ref<string | null>(null);
const { width: windowWidth } = useWindowSize();

const visiblePanels = computed(() => panels.value.filter((p) => p.visible && !p.isOverlay));

const registerPanel = (panel: SplitterPanel) => {
  const index = panels.value.findIndex((p) => p.id === panel.id);
  if (index === -1) {
    panels.value.push(panel);
  } else {
    panels.value[index] = { ...panel };
  }
  updateGridTemplate();
};

const unregisterPanel = (panelId: string) => {
  panels.value = panels.value.filter((p) => p.id !== panelId);
  updateGridTemplate();
};

const updatePanelSizes = (sizes: Record<string, number>) => {
  let changed = false;
  for (const [panelId, size] of Object.entries(sizes)) {
    const panel = panels.value.find((p) => p.id === panelId);
    if (!panel) continue;
    const minSize = panel.minSize ?? 0;
    const maxSize = panel.maxSize ?? Infinity;
    panel.size = Math.max(minSize, Math.min(maxSize, size));
    changed = true;
  }
  if (!changed) return;
  updateGridTemplate();
  emit("resize", { panels: [...panels.value] });
};

const onResizeStart = (panelId: string) => {
  isResizing.value = true;
  currentResizingPanel.value = panelId;
};

const onResizeEnd = () => {
  isResizing.value = false;
  currentResizingPanel.value = null;
};

const splitterContext: SplitterContext = {
  registerPanel,
  unregisterPanel,
  updatePanelSizes,
  onResizeStart,
  onResizeEnd,
  get panels() {
    return readonly(panels.value);
  },
};

provide<SplitterContext>("splitterContext", splitterContext);

watch(
  () => visiblePanels.value.length,
  () => redistributeSpace(),
);

const redistributeSpace = () => {
  if (!containerRef.value) return;

  const containerWidth = windowWidth.value;
  const splitterSize = 12;
  const splitterCount = visiblePanels.value.length - 1;
  const totalSplitterWidth = splitterCount * splitterSize;

  const availableWidth = containerWidth - totalSplitterWidth;

  let fixedWidth = 0;

  visiblePanels.value.forEach((panel) => {
    if (panel.size) {
      fixedWidth += panel.size;
    } else {
      fixedWidth += panel.minSize ?? 0;
    }
  });

  if (fixedWidth > availableWidth) {
    const panelsToShrink = visiblePanels.value.filter((p) => !p.collapsible && p.size && p.size > (p.minSize ?? 0));

    if (panelsToShrink.length > 0) {
      const reduction = fixedWidth - availableWidth;

      panelsToShrink.sort((a, b) => (b.size ?? 0) - (a.size ?? 0));

      let remainingReduction = reduction;

      for (const panel of panelsToShrink) {
        const minSize = panel.minSize ?? 0;
        const currentSize = panel.size ?? 0;
        const maxReduction = currentSize - minSize;

        if (maxReduction > 0) {
          const actualReduction = Math.min(remainingReduction, maxReduction);
          panel.size = currentSize - actualReduction;
          remainingReduction -= actualReduction;

          if (remainingReduction <= 0) break;
        }
      }
    }
  }

  updateGridTemplate();
};

const getCollapsedSizes = (inFlowPanels: SplitterPanel[]): Map<string, number> => {
  const splitterSize = 12;
  const requiredWidth =
    inFlowPanels.reduce((sum, p) => sum + (p.size ?? p.minSize ?? 0), 0) +
    Math.max(inFlowPanels.length - 1, 0) * splitterSize;

  let overflow = requiredWidth - windowWidth.value;
  const collapsedSizes = new Map<string, number>();

  for (const panel of inFlowPanels) {
    if (overflow <= 0) break;
    if (!panel.collapsible || !panel.size) continue;

    const reduction = Math.min(overflow, panel.size);
    collapsedSizes.set(panel.id, panel.size - reduction);
    overflow -= reduction;
  }

  return collapsedSizes;
};

let lastGridTemplate: string | undefined;

const updateGridTemplate = () => {
  if (!containerRef.value) return;

  const orderedPanels = panels.value
    .filter((p) => (p.visible || p.isTransitioning) && !p.isOverlay)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const splitterSize = "12px";
  const collapsedSizes = getCollapsedSizes(orderedPanels.filter((p) => p.visible));

  const templateParts: string[] = [];

  orderedPanels.forEach((panel, index) => {
    if (!panel.visible && panel.isTransitioning) {
      templateParts.push(`0`);
    } else if (panel.size !== undefined && panel.size !== null) {
      if (panel.isOverlay) {
        templateParts.push(`0`);
      } else {
        templateParts.push(`${collapsedSizes.get(panel.id) ?? panel.size}px`);
      }
    } else if (panel.minSize) {
      templateParts.push(`minmax(${panel.minSize}px, 1fr)`);
    } else {
      templateParts.push("1fr");
    }

    // Add splitter only between visible panels
    if (index < orderedPanels.length - 1) {
      templateParts.push(panel.isTransitioning && !panel.visible ? `0` : splitterSize);
    }
  });

  const template = templateParts.join(" ");
  if (template === lastGridTemplate) return;
  lastGridTemplate = template;

  containerRef.value.style.gridTemplateColumns = template;
  containerRef.value.style.gridTemplateRows = "100%";
};

const containerStyle = computed(() => ({
  display: "grid",
  width: "100%",
  height: "100%",
  gap: "0px",
}));

watch(windowWidth, () => {
  if (!isResizing.value) {
    updateGridTemplate();
  }
});

onMounted(() => {
  updateGridTemplate();
});

defineExpose({
  updateGridTemplate,
  panels: readonly(panels),
});
</script>

<style lang="scss" module>
.splitter {
  position: relative;
  overflow: hidden;
  transition: 0.05s;

  &.resizing {
    // The browser animates grid-template-columns, so a transition makes the panels trail the cursor.
    transition: none;
    user-select: none;

    :global(iframe) {
      pointer-events: none !important;
    }
  }
}
</style>
